/**
 * Turkish text for coverage findings, built from structured fields.
 *
 * The backend writes an `explanation` for every issue. It is English, written
 * for whoever is reading logs, and real UI validation caught sentences like
 * "Everything this project has captured of the left side..." sitting in the
 * middle of a Turkish screen. That is the bug this file exists to fix.
 *
 * The fix is not translation. Machine-translating backend prose, or
 * pattern-matching English sentences, would make the UI's correctness depend
 * on wording nobody controls - a backend copy edit would silently change what
 * the user is told. Instead each message is composed here from fields that are
 * already structured and already part of the contract:
 *
 *     code + region + measured_value + threshold + severity
 *
 * Two rules follow from that, and both matter:
 *
 * **The numbers come from the API, never from arithmetic done here.** A
 * threshold recomputed in React is a second implementation of a product rule,
 * and the two will disagree eventually.
 *
 * **An unrecognised code falls back to Turkish, never to the English prose.**
 * A future backend release must not be able to leak English onto this screen
 * just by adding an issue type.
 */

import type { CoverageRegion, ProjectCoverageIssue } from "@/api/types";
import { COVERAGE_REGION_LABELS } from "./terms";

/** What a finding says, and what to do about it. */
export interface IssuePresentation {
  title: string;
  /** Optional second line. Omitted when the title says everything. */
  detail?: string | undefined;
}

const regionName = (region: CoverageRegion | null | undefined): string =>
  region ? COVERAGE_REGION_LABELS[region] : "Bu bölge";

/**
 * The measured value against its threshold, in the unit that value actually has.
 *
 * This needed correcting after a live response was inspected. The per-region
 * findings carry `measured_value: 0.0663, threshold: 0.2` - those are **coverage
 * scores on a 0-1 scale**, not viewpoint counts. An earlier version of this
 * function rendered them as "0.07 bağımsız görüş bulundu; hedef 0.20", which is
 * not merely clumsy: it states a measurement the system never made, which is
 * the exact class of error this whole phase is about.
 *
 * So the unit is a parameter, and each code says which one it means.
 * `measured_value` and `threshold` are both optional in the contract, so every
 * path survives their absence rather than printing "undefined".
 */
function measuredAgainstThreshold(
  issue: ProjectCoverageIssue,
  unit: "ratio" | "count",
): string | undefined {
  const { measured_value: measured, threshold } = issue;
  if (measured === null || measured === undefined) return undefined;

  const show = (value: number): string =>
    unit === "ratio"
      ? `%${Math.round(value * 100)}`
      : Number.isInteger(value)
        ? String(value)
        : value.toFixed(2);

  const noun = unit === "ratio" ? "Ölçülen kapsama" : "Bulunan";
  if (threshold === null || threshold === undefined) {
    return `${noun} ${show(measured)}.`;
  }
  return `${noun} ${show(measured)}; hedef ${show(threshold)}.`;
}

type Builder = (issue: ProjectCoverageIssue) => IssuePresentation;

/**
 * The five per-region shortfalls share a shape, so they share a builder.
 *
 * Note the wording: "yeterli farklı açı bulunamadı", not "bu bölgeyi
 * çekmediniz". The finding is about viewpoint diversity in what was measured.
 * A real capture contained a clear left profile the analysis could not read,
 * and telling that user they had not photographed it would have been false.
 */
const insufficientRegion: Builder = (issue) => ({
  title: `${regionName(issue.region)} için yeterli farklı açı bulunamadı.`,
  // A 0-1 coverage score, shown as a percentage. Verified against a live
  // response: measured_value 0.0663 against threshold 0.2.
  detail: measuredAgainstThreshold(issue, "ratio"),
});

const BUILDERS: Record<string, Builder> = {
  PROJECT_FRONT_FACE_INSUFFICIENT_COVERAGE: insufficientRegion,
  PROJECT_LEFT_SIDE_INSUFFICIENT_COVERAGE: insufficientRegion,
  PROJECT_RIGHT_SIDE_INSUFFICIENT_COVERAGE: insufficientRegion,
  PROJECT_LEFT_EAR_INSUFFICIENT_COVERAGE: insufficientRegion,
  PROJECT_RIGHT_EAR_INSUFFICIENT_COVERAGE: insufficientRegion,

  PROJECT_COVERAGE_UNKNOWN: (issue) => ({
    title: `${regionName(issue.region)} değerlendirilemedi.`,
    // The distinction the whole product rests on: nothing could measure it,
    // which is not the same as measuring it and finding it wanting.
    detail:
      "Bu bölgeyi ölçebilecek bir görüntü bulunamadı. Bölgenin çekilmediği anlamına gelmez.",
  }),

  PROJECT_LOW_COVERAGE_CONFIDENCE: (issue) => ({
    title: "Bu değerlendirme sınırlı ölçülebilir kanıta dayanıyor.",
    // Also a 0-1 ratio: the confidence in the verdict, against its floor.
    detail:
      measuredAgainstThreshold(issue, "ratio") ??
      "Karelerin çoğu ölçülemediği için sonucun güvenilirliği düşük.",
  }),

  CORRECTION_ADDED_NO_USEFUL_COVERAGE: (issue) => ({
    title: `Düzeltme çekimi ${regionName(issue.region).toLocaleLowerCase("tr-TR")} kapsamasını artırmadı.`,
    detail: "Yeni çekim, daha önce elde edilmiş açılardan farklı bir görüş eklemedi.",
  }),

  CONFLICTING_COVERAGE_EVIDENCE: (issue) => ({
    title: `${regionName(issue.region)} için çekimler birbiriyle çelişiyor.`,
    detail: "Farklı oturumlar bu bölge hakkında farklı sonuçlar verdi.",
  }),

  SOURCE_SESSIONS_NOT_ANALYSED: (issue) => ({
    title: "Bazı çekim oturumları analiz edilmediği için dahil edilmedi.",
    // A count of sessions, not a ratio.
    detail:
      issue.measured_value !== null && issue.measured_value !== undefined
        ? `${issue.measured_value} oturum analiz edilmeyi bekliyor.`
        : undefined,
  }),
};

/**
 * The safe fallback for a code this build has never heard of.
 *
 * Deliberately says nothing about the subject. Guessing from the code name
 * would be the same mistake as translating the prose, one step removed.
 */
export const UNKNOWN_ISSUE_TEXT = "Bu bulgu için ayrıntılı açıklama henüz mevcut değil.";

export function presentIssue(issue: ProjectCoverageIssue): IssuePresentation {
  const builder = BUILDERS[issue.code];
  if (!builder) {
    return {
      title: UNKNOWN_ISSUE_TEXT,
      ...(issue.region ? { detail: regionName(issue.region) } : {}),
    };
  }
  return builder(issue);
}

/** Every code this build renders deliberately. Used by the regression test. */
export const KNOWN_ISSUE_CODES = Object.keys(BUILDERS);
